// Assessment API Types and Interfaces

// Base Assessment Payload Interface
export interface AssessmentPayload {
  gender: string;
  race: string;
  date_of_birth?: string;
  height_in: string;
  // Height fields - conditional based on unit
  height_ft?: number; // Only when height_in is "ft_in"
  height_inches?: number; // Only when height_in is "ft_in"
  height_cm?: number; // Only when height_in is "cm"
  weight_in: string;
  // Weight fields - conditional based on unit
  weight_kg?: number; // Only when weight_in is "kg"
  weight_lbs?: number; // Only when weight_in is "lbs"
  // Target Weight fields - optional, conditional based on unit
  target_weight_in?: string;
  target_weight_kg?: number; // Only when target_weight_in is "kg"
  target_weight_lbs?: number; // Only when target_weight_in is "lbs"
  diabetes_type: string;
  weight_loss_goal: string;
  weight_loss_reasons?: string; // Optional - only when weight_loss_goal is 'yes'
  activity_level: string;
  diet_adherence: string;
  intermittent_fasting: string;
  fasting_window_type?: string; // Optional - only when intermittent_fasting is 'Yes — I follow a fasting window'
  meal_times: string[];
  medication_details: MedicationDetail[];
  insulin_details: InsulinDetail[];
}

// Medication Detail Interface
export interface MedicationDetail {
  name: string;
  dose: string;
  frequency: string;
  time: string;
  quantity: number;
}

// Insulin Detail Interface
export interface InsulinDetail {
  dose_type: string;
  insulin_type: string;
  timing: string;
  dose: string;
  brand: string;
}

// API Response Interfaces
export interface AssessmentAPIResponse {
  success: boolean;
  message?: string;
  data?: any;
  errors?: Record<string, string[]>;
  detail?: string;
  non_field_errors?: string[];
}

// Session Data Interface (from frontend forms)
export interface AssessmentSessionData {
  // Basic Info
  gender?: string;
  race?: string;
  age?: number;

  // Height measurements
  height_ft?: number;
  height_inches?: number;
  height_cm?: number;

  // Weight measurements
  weight_kg?: number;
  weight_lbs?: number;
  weight?: number;
  weight_unit?: string;

  // Assessment fields
  diabetes_type?: string;
  type_of_diabetes?: string;
  weight_loss_goal?: string;
  weight_loss?: string;
  weight_loss_reasons?: string;
  activity_level?: string;
  workout_frequency?: string;
  diet_adherence?: string;
  intermittent_fasting?: string | boolean;
  fasting_window_type?: string;
  meal_times?: string[];
  is_completed?: boolean;

  // Details arrays
  medication_details?: MedicationDetail[];
  insulin_details?: InsulinDetail[];
}

// Validation Result Interface
export interface ValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
}

// Transformation Options
export interface TransformationOptions {
  validateRequired?: boolean;
  useDefaults?: boolean;
  normalizeValues?: boolean;
}

// Cache Entry Interface
export interface AssessmentCacheEntry {
  payload: AssessmentPayload;
  response: AssessmentAPIResponse;
  timestamp: number;
  hash: string;
}

// Normalization Maps
export const GENDER_MAP: Record<string, string> = {
  Male: "male",
  Female: "female",
  Other: "other",
} as const;

export const RACE_MAP: Record<string, string> = {
  white: "white",
  black: "black_african_american",
  asian: "asian",
  native: "american_indian_alaska_native",
  pacific: "native_hawaiian_pacific_islander",
  middle_east: "middle_eastern_north_african",
  mixed: "other",
  other: "other",
} as const;

export const ACTIVITY_LEVEL_MAP: Record<string, string> = {
  sedentary: "sedentary",
  lightly_active: "lightly_active",
  moderately_active: "moderately_active",
  very_active: "very_active",
  extremely_active: "extremely_active",
} as const;

export const WEIGHT_LOSS_GOAL_MAP: Record<string, string> = {
  Yes: "yes",
  No: "no",
  "Not Sure": "not_sure",
} as const;

export const INTERMITTENT_FASTING_MAP: Record<string, string> = {
  "Yes — I follow a fasting window": "Yes — I follow a fasting window",
  "Sometimes / Not sure": "Sometimes / Not sure",
  "No — I eat throughout the day": "No — I eat throughout the day",
} as const;

export const FASTING_WINDOW_MAP: Record<string, string> = {
  "16:8 (Fast 16 hrs / Eat 8 hrs)": "16:8 (Fast 16 hrs / Eat 8 hrs)",
  "18:6": "18:6",
  "14:10": "14:10",
  Custom: "Custom",
  "My eating window varies": "My eating window varies",
} as const;
